<template>
  <div ref="BmControlRef" class="bm-field" :data-qa="undefined">
    <label class="bm-field-label">{{
      (showRequiredIndicator ? "* " : "") + labelVal
    }}</label>
    <template v-if="showTooltip && tooltipVal">
      <bm-tooltip bottom>
        <template #activator="{ on }">
          <v-icon color="primaryChip" small v-on="on"> info </v-icon>
        </template>
        <span>{{ tooltipVal }}</span>
      </bm-tooltip>
    </template>
    <beta-icon v-if="isBeta" />
    <slot v-if="!readOnly" :data-qa="dataQa" />
    <slot v-else name="readOnly" :data-qa="dataQa">
      <v-row class="ma-0">
        <v-col cols="12" class="pa-0 d-flex">
          <slot name="prepend-inner" :read-only="readOnly" />

          <span v-if="routerLocation">
            <router-link :to="routerLocation">
              <pre class="read-only" :data-qa="dataQa"><v-progress-linear
v-if="loading"
      class="mb-0 mt-2"
      indeterminate
      color="text02"
          /><slot v-else name="readOnlyValue">{{
                displayString(readOnlyValue)
              }}</slot></pre>
            </router-link>
          </span>
          <span v-else-if="externalLocation">
            <a
              :href="externalWithHttp"
              target="_blank"
              class="external-link"
              rel="noopener noreferrer"
            >
              <pre class="read-only" :data-qa="dataQa"><v-progress-linear
v-if="loading"
      class="mb-0 mt-2"
      indeterminate
      color="text02"
          /><slot v-else name="readOnlyValue">{{
                displayString(readOnlyValue)
              }}</slot></pre>
            </a>
          </span>
          <span v-else>
            <pre class="read-only" :data-qa="dataQa"><v-progress-linear
            v-if="loading"
            class="mb-0 mt-2"
            indeterminate
            color="text02"
          /><slot v-else name="readOnlyValue">{{
            displayString(readOnlyValue)
          }}</slot></pre>
          </span>
          <slot name="trailingAction"
            ><bm-btn
              v-if="showCopyClipboardAction && readOnlyValue"
              icon
              button-type="ghost"
              btn-class="clipboard-btn-field"
              :data-clipboard-text="readOnlyValue"
              ><v-icon small>assignment</v-icon></bm-btn
            >
            <bm-btn
              v-else-if="showCopyClipboardActionSimple && readOnlyValue"
              icon
              button-type="ghost"
              btn-class="clipboard-btn-field"
              @click="writeClipboardText(readOnlyValue)"
              ><v-icon small>assignment</v-icon></bm-btn
            ></slot
          >
        </v-col>
      </v-row>
    </slot>
  </div>
</template>

<script lang="ts">
/**
 * This is how you define additional component options while using
 * script setup.
 *
 * https://vuejs.org/api/sfc-script-setup.html#usage-alongside-normal-script
 *
 * In Vue 3.3+, this can be replaced by defineOptions()
 */
export default defineComponent({
  name: "BmControl",
  inheritAttrs: false,
});
</script>

<script setup lang="ts">
import BmTooltip from "@/components/controls/input/BmTooltip.vue";
import type { Location } from "vue-router";
import { useBmLabelled } from "@/components/controls/input/mixins/BmLabelled";
import BetaIcon from "@/components/controls/BetaIcon.vue";
import { useDisplayHelpers } from "@/components/mixins/DisplayHelpers";
import { defineComponent, computed, onMounted, ref } from "vue";

interface BmControlProps {
  // BmLabelledProps
  label?: string | null;
  tooltip?: string | null;
  // TODO - figure out why errors appear in console when using
  // Key here instead of object
  t?: object | string;

  rules?: any[];
  readOnly?: boolean;
  readOnlyValue?: string | null;
  showCopyClipboardAction?: boolean;
  showCopyClipboardActionSimple?: boolean;
  showTooltip?: boolean;
  routerLocation?: Location;
  externalLocation?: string;
  loading?: boolean;
  isBeta?: boolean;
  dataQa?: object | string;
}

const props = withDefaults(defineProps<BmControlProps>(), {
  // BmLabelledProps defaults
  label: null,
  tooltip: null,

  // BmControlProps defaults
  readOnly: false,
  showCopyClipboardAction: false,
  showCopyClipboardActionSimple: false,
  showTooltip: true,
  loading: false,
  isBeta: false,
});

const { dataQa, tooltipVal, labelVal } = useBmLabelled({
  tooltip: computed(() => props.tooltip),
  label: computed(() => props.label),
  t: computed(() => props.t),
  dataQa: computed(() => props.dataQa),
});

const { displayString } = useDisplayHelpers();

const BmControlRef = ref<any>(null);

const showRequiredIndicator = computed(() => {
  return !!(
    !props.readOnly &&
    labelVal.value?.trim() &&
    props.rules?.some((rule: any) => rule.showRequiredIndicator)
  );
});

const externalWithHttp = computed(() => {
  return props.externalLocation?.startsWith("http")
    ? props.externalLocation
    : `//${props.externalLocation}`; // Prepending with // will cause this not to open as a sublink and add http for us
});

const writeClipboardText = (text?: string) => {
  if (!text) {
    return;
  }
  navigator.clipboard.writeText(text);
};

onMounted(() => {
  // This is hacky but seems the only reliable way to prevent data attribute
  // inheritance on this component (that i could figure anyway).
  BmControlRef.value?.setAttribute("data-qa", "");
});
</script>

<style scoped lang="sass">
@import "src/sass/typography"
.bm-field
  @include bm-field

.v-icon
  margin-left: 5px
  cursor: pointer
.clipboard-btn-field .v-icon
  margin-left: 0
:deep(.clipboard-btn-field)
  margin-top: -5px
.read-only
  @include bm-body
  font-family: $body-font-family
  white-space: pre-wrap
  word-break: break-word
  max-height: 5rem
  overflow-y: auto
  line-height: normal
</style>
